import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { EntitlementSwitch } from '../../components/EntitlementSwitch';
import { Icon } from '../../components/Icon';
import { ProjectReplacementDialog } from '../../components/ProjectReplacementDialog';
import { DropmapProjectError } from '../../project/dropmap/errors';
import { PROJECT_FILE_ACCEPT } from '../../project/dropmap/filename';
import placeioLogoUrl from '../../../docs/brand/reference/placeio-logo.svg';
import { ModeNavigation } from '../modes/ModeNavigation';
import { useAppStore } from '../state/useAppStore';
import { nextPaint } from '../../operations/longOperation';

type ProjectActionId = 'new-map' | 'open-project' | 'save-project';

interface ProjectAction {
  readonly id: ProjectActionId;
  readonly label: string;
}

const AVAILABLE_PROJECT_ACTIONS: readonly ProjectAction[] = [
  { id: 'new-map', label: 'New Map' },
  { id: 'save-project', label: 'Save Project' },
  { id: 'open-project', label: 'Open Project…' },
];

const OPEN_PROJECT_SUCCESS_DURATION_MS = 3500;

interface ProjectFileMessage {
  readonly kind: 'error' | 'status';
  readonly text: string;
  readonly dismissAfterMs?: number;
}

export function ProjectTopBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [replacementAction, setReplacementAction] = useState<'new-map' | 'open-project' | null>(
    null,
  );
  const [replacementReturnFocus, setReplacementReturnFocus] = useState<HTMLElement | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [message, setMessage] = useState<ProjectFileMessage | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const projectMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const projectInputRef = useRef<HTMLInputElement | null>(null);
  const project = useAppStore((state) => state.project);
  const title = project.metadata.title;
  const hasProject = useAppStore((state) => state.mode !== 'empty');
  const newMap = useAppStore((state) => state.newMap);
  const replaceProject = useAppStore((state) => state.replaceProject);

  useEffect(() => {
    if (!isMenuOpen) return;

    const dismiss = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setIsMenuOpen(false);
    };
    const dismissOnFocusOutside = (event: FocusEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setIsMenuOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      setIsMenuOpen(false);
      requestAnimationFrame(() => projectMenuTriggerRef.current?.focus());
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('focusin', dismissOnFocusOutside);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('focusin', dismissOnFocusOutside);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isMenuOpen]);

  useLayoutEffect(() => {
    if (!isMenuOpen) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [isMenuOpen]);

  useEffect(() => {
    if (message?.dismissAfterMs === undefined) return;

    const timeout = window.setTimeout(() => {
      setMessage((current) => (current === message ? null : current));
    }, message.dismissAfterMs);
    return () => window.clearTimeout(timeout);
  }, [message]);

  const saveProject = async () => {
    setMessage({ kind: 'status', text: 'Saving project locally…' });
    setIsWorking(true);
    try {
      await nextPaint();
      const { downloadDropmapProjectAsync } = await import('../../project/dropmap/browserFile');
      const filename = await downloadDropmapProjectAsync(project);
      setMessage({ kind: 'status', text: `Saved ${filename}` });
    } catch (error) {
      setMessage({
        kind: 'error',
        text:
          error instanceof DropmapProjectError
            ? error.userMessage
            : 'Placeio Maps could not save this project. Try again.',
      });
    } finally {
      setIsWorking(false);
    }
  };

  const runAction = (action: ProjectAction) => {
    setIsMenuOpen(false);
    setMessage(null);
    if (action.id === 'save-project') {
      void saveProject();
      requestAnimationFrame(() => projectMenuTriggerRef.current?.focus());
      return;
    }
    setReplacementReturnFocus(projectMenuTriggerRef.current);
    setReplacementAction(action.id);
  };

  const handleMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = [
      ...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []),
    ];
    if (items.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    const currentIndex = items.findIndex((item) => item === document.activeElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowUp'
            ? (currentIndex - 1 + items.length) % items.length
            : (currentIndex + 1) % items.length;
    items[nextIndex]?.focus();
  };

  const handleProjectInput = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.item(0);
    event.currentTarget.value = '';
    if (!file) return;

    setMessage({ kind: 'status', text: 'Opening project locally…' });
    setIsWorking(true);
    try {
      const { readDropmapProjectFile } = await import('../../project/dropmap/browserFile');
      const openedProject = await readDropmapProjectFile(file);
      replaceProject(openedProject);
      setMessage({
        kind: 'status',
        text: `Opened ${file.name}`,
        dismissAfterMs: OPEN_PROJECT_SUCCESS_DURATION_MS,
      });
    } catch (error) {
      setMessage({
        kind: 'error',
        text:
          error instanceof DropmapProjectError
            ? error.userMessage
            : 'Placeio Maps could not open this project. Try again.',
      });
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <>
      <header className="project-top-bar">
        <div className="top-bar-left">
          <div className="app-brand" role="img" aria-label="Placeio Maps">
            <img alt="" className="brand-logo" src={placeioLogoUrl} />
          </div>
          {hasProject ? (
            <div className="project-menu" ref={menuRef}>
              <button
                aria-expanded={isMenuOpen}
                aria-haspopup="menu"
                className="project-menu-trigger"
                disabled={isWorking}
                onClick={() => setIsMenuOpen((open) => !open)}
                ref={projectMenuTriggerRef}
                type="button"
              >
                <span>{title}</span>
                <Icon name="chevron-down" />
              </button>
              {isMenuOpen ? (
                <div
                  aria-label="Project actions"
                  className="menu-popover project-menu-popover"
                  onKeyDown={handleMenuKeyDown}
                  role="menu"
                >
                  {AVAILABLE_PROJECT_ACTIONS.map((action) => (
                    <button
                      disabled={isWorking}
                      key={action.id}
                      onClick={() => runAction(action)}
                      role="menuitem"
                      type="button"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {hasProject ? (
            <input
              accept={PROJECT_FILE_ACCEPT}
              aria-label="Choose a Placeio project file"
              className="visually-hidden"
              disabled={isWorking}
              onChange={(event) => void handleProjectInput(event)}
              ref={projectInputRef}
              tabIndex={-1}
              type="file"
            />
          ) : null}
        </div>

        {hasProject ? <ModeNavigation /> : <div aria-hidden="true" />}

        <div className="top-bar-right">
          <EntitlementSwitch />
        </div>
      </header>
      {message ? (
        <p
          className={`project-file-message project-file-message-${message.kind}`}
          role={message.kind === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}
      {replacementAction ? (
        <ProjectReplacementDialog
          confirmLabel={replacementAction === 'new-map' ? 'Start new map' : 'Open project'}
          description="Your current map will be replaced."
          onCancel={() => {
            setReplacementAction(null);
            setReplacementReturnFocus(null);
          }}
          onConfirm={() => {
            const action = replacementAction;
            setReplacementAction(null);
            setReplacementReturnFocus(null);
            if (action === 'new-map') {
              newMap();
            } else {
              projectInputRef.current?.click();
            }
          }}
          returnFocusTo={replacementReturnFocus}
          title={replacementAction === 'new-map' ? 'Start a new map?' : 'Open another project?'}
        />
      ) : null}
    </>
  );
}
